import type { CoupleSpace, EventAction, Ticket, TicketCommand } from '../../domain/types';
import { appendShare } from './create-view';
import { statusLabels } from './home-view';

export interface TicketDetailModel {
  space: CoupleSpace;
  ticket: Ticket;
  shareUrl?: string;
  shareCard?: Blob;
  cardError?: string;
  notice?: string;
  error?: string;
  localImage?: Blob | null;
}

export interface TicketDetailActions {
  onAct?: (action: TicketCommand['action'], note?: string) => void | Promise<void>;
  onShare?: (url: string, card: Blob | null, status: HTMLElement) => void | Promise<void>;
  onDeleteLocal?: () => void | Promise<void>;
  onCopyAsNew?: () => void;
}

export function renderTicketDetail(
  container: HTMLElement,
  model: TicketDetailModel,
  actions: TicketDetailActions = {},
): void {
  container.replaceChildren();
  const ticket = model.ticket;
  const section = document.createElement('article');
  section.className = `page-card stack ticket-detail ticket-detail--${ticket.type}`;
  section.innerHTML = `
    <header class="stack">
      <p class="eyebrow">${ticket.type === 'reward' ? '🎁 奖励单' : '📝 小罚单'}</p>
      <h1>${escapeText(ticket.title)}</h1>
      <span class="status-badge status-badge--${ticket.state}">${statusLabels[ticket.state]}</span>
    </header>
    <dl class="ticket-facts">
      <div><dt>详细说明</dt><dd>${escapeText(ticket.description || '—')}</dd></div>
      <div><dt>${ticket.type === 'reward' ? '奖励内容' : '惩罚内容'}</dt><dd>${escapeText(ticket.outcome)}</dd></div>
      <div><dt>截止日期</dt><dd>${escapeText(ticket.dueDate ?? '未设置')}</dd></div>
      <div><dt>重要程度</dt><dd>${importanceLabel(ticket.importance)}</dd></div>
      <div><dt>标签</dt><dd>${escapeText(ticket.tags.join(' · ') || '—')}</dd></div>
      <div><dt>署名</dt><dd>${escapeText(ticket.signature)}</dd></div>
      <div><dt>当前责任方</dt><dd>${escapeText(responsibility(model.space, ticket))}</dd></div>
    </dl>
    <section class="stack" aria-labelledby="timeline-title">
      <h2 id="timeline-title">时间线</h2>
      <ol class="timeline"></ol>
    </section>
    <div class="ticket-actions stack" data-ticket-actions></div>
  `;
  const timeline = section.querySelector<HTMLOListElement>('.timeline')!;
  for (const event of ticket.events) {
    const item = document.createElement('li');
    const actor = event.actor === model.space.memberId ? model.space.selfName : model.space.partnerName;
    item.innerHTML = `<b>${eventLabel(event.action, ticket.type)}</b><span>${escapeText(actor)} · ${formatTime(event.at)}</span>`;
    if (event.note) {
      const note = document.createElement('p');
      note.textContent = event.note;
      item.append(note);
    }
    timeline.append(item);
  }

  if (model.notice) appendNotice(section, model.notice, 'status');
  if (model.error) appendNotice(section, model.error, 'alert');
  const actionRoot = section.querySelector<HTMLElement>('[data-ticket-actions]')!;
  renderAllowedActions(actionRoot, model, actions);
  if (model.localImage) {
    const image = document.createElement('img');
    image.className = 'ticket-detail__image';
    image.alt = '本机照片（不会进入分享链接）';
    image.src = URL.createObjectURL(model.localImage);
    section.insertBefore(image, actionRoot);
  }
  const localActions = document.createElement('div');
  localActions.className = 'ticket-actions stack';
  localActions.append(actionButton('删除本机副本', 'button button--danger', () => actions.onDeleteLocal?.()));
  if (ticket.state === 'rejected') {
    localActions.append(actionButton('复制内容新建', 'button', () => actions.onCopyAsNew?.()));
  }
  const localNotice = document.createElement('p');
  localNotice.className = 'muted';
  localNotice.textContent = '删除只会清除这台设备上的单据和本机照片，不会撤回对方设备上的记录。';
  localActions.append(localNotice);
  section.append(localActions);
  if (model.shareUrl) {
    const next = document.createElement('p');
    next.className = 'next-step';
    next.textContent = `下一步：复制链接并发给 ${model.space.partnerName}`;
    section.append(next);
    appendShare(section, model.shareUrl, model.shareCard ?? null, model.cardError, actions);
  }
  container.append(section);
}

function renderAllowedActions(
  root: HTMLElement,
  model: TicketDetailModel,
  actions: TicketDetailActions,
): void {
  const { space, ticket } = model;
  if (model.shareUrl) return;
  if (space.memberId === ticket.recipientId && ticket.state === 'pending_response') {
    const accept = actionButton('接受这张单', 'button--primary', () => actions.onAct?.('accept'));
    const form = document.createElement('form');
    form.className = 'stack reject-form';
    form.innerHTML = '<label class="field">拒绝原因<textarea name="note" maxlength="120" rows="3" required></textarea></label><button class="button button--danger" type="submit">拒绝并说明原因</button>';
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const data = new FormData(form);
      void actions.onAct?.('reject', formString(data, 'note'));
    });
    root.append(accept, form);
    return;
  }
  if (space.memberId === ticket.recipientId && ticket.state === 'accepted') {
    const form = document.createElement('form');
    form.className = 'stack';
    form.innerHTML = `<label class="field">回应<textarea name="note" maxlength="120" rows="3"></textarea></label><button class="button button--primary" type="submit">${ticket.type === 'reward' ? '申请兑现' : '提交已完成'}</button>`;
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const data = new FormData(form);
      void actions.onAct?.('submit', formString(data, 'note') || undefined);
    });
    root.append(form);
    return;
  }
  if (space.memberId === ticket.creatorId && ticket.state === 'pending_confirmation') {
    root.append(actionButton(
      ticket.type === 'reward' ? '确认已兑现' : '确认已完成',
      'button--primary',
      () => actions.onAct?.('confirm'),
    ));
  }
}

function actionButton(label: string, className: string, action: () => unknown): HTMLButtonElement {
  const button = document.createElement('button');
  button.className = `button ${className}`;
  button.type = 'button';
  button.textContent = label;
  button.addEventListener('click', () => void action());
  return button;
}

function responsibility(space: CoupleSpace, ticket: Ticket): string {
  if (ticket.state === 'rejected' || ticket.state === 'completed') return '这张单已结束';
  const memberId = ticket.state === 'pending_confirmation' ? ticket.creatorId : ticket.recipientId;
  return memberId === space.memberId ? `${space.selfName}（本机）` : space.partnerName;
}

function eventLabel(action: EventAction, type: Ticket['type']): string {
  const labels: Record<EventAction, string> = {
    created: '创建单据',
    accepted: '接受单据',
    rejected: '拒绝单据',
    submitted: type === 'reward' ? '申请兑现' : '提交已完成',
    confirmed: type === 'reward' ? '确认已兑现' : '确认已完成',
    conflict_resolved: '解决冲突',
  };
  return labels[action];
}

function importanceLabel(value: Ticket['importance']): string {
  return value === 'important' ? '很重要' : value === 'promise' ? '认真约定' : '小小心意';
}

function formatTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? escapeText(value) : date.toLocaleString('zh-CN');
}

function appendNotice(section: HTMLElement, text: string, role: 'status' | 'alert'): void {
  const notice = document.createElement('p');
  if (role === 'alert') {
    notice.id = 'ticket-detail-error';
    section.setAttribute('aria-describedby', notice.id);
  }
  notice.className = role === 'alert' ? 'error' : 'success-notice';
  notice.setAttribute('role', role);
  notice.textContent = text;
  section.append(notice);
}

function formString(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === 'string' ? value : '';
}

function escapeText(value: string): string {
  const node = document.createElement('span');
  node.textContent = value;
  return node.innerHTML;
}
